import React, { useEffect, useState } from "react";
import {
  Bot,
  Brain,
  CalendarCheck,
  ChevronDown,
  ChevronRight,
  CircleAlert,
  Download,
  FileText,
  Loader2,
  Terminal,
  TrendingUp,
} from "lucide-react";
import { downloadArtifact } from "@/shared/utils/artifactDownload";
import { CodeBlock, CodeBlockCode } from "./CodeBlock";

const AGENT_META = {
  manager: { label: "Manager", Icon: Brain },
  reasoning: { label: "Reasoning", Icon: Brain },
  meeting_prep: { label: "Meeting Prep Agent", Icon: CalendarCheck },
  upsell_cross_sell: { label: "Upsell / Cross-sell Agent", Icon: TrendingUp },
  create_proposal: { label: "Proposal Agent", Icon: FileText },
};

/** Group consecutive steps by the agent that produced them. */
function groupByAgent(steps) {
  const groups = [];
  for (const step of steps) {
    const agent = step.agent || "manager";
    const last = groups[groups.length - 1];
    if (last && last.agent === agent) {
      last.steps.push(step);
    } else {
      groups.push({ agent, steps: [step] });
    }
  }
  return groups;
}

/** Claude-style expandable tool payload: collapsed by default, one click to
 * reveal the code the agent wrote or the command it is running. */
function StepData({ data }) {
  const [open, setOpen] = useState(false);
  if (!data?.kind) return null;

  if (data.kind === "artifact") {
    const fileType = data.file_type || "pptx";
    return (
      <button
        type="button"
        onClick={() => downloadArtifact(data.download_url, `proposal.${fileType}`)}
        className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
      >
        <Download size={13} />
        Download {String(fileType).toUpperCase()}
      </button>
    );
  }

  const isCode = data.kind === "code";
  const isCommand = data.kind === "command";
  if (!isCode && !isCommand) return null;
  const label = isCode
    ? `View code${data.language ? ` · ${data.language}` : ""}`
    : "View command";

  return (
    <div className="mt-1">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-400 transition-colors hover:text-gray-700"
      >
        <ChevronRight
          size={12}
          className={`transition-transform ${open ? "rotate-45" : ""}`}
          aria-hidden="true"
        />
        {isCommand && <Terminal size={11} />}
        {label}
      </button>
      {open &&
        (isCode ? (
          <CodeBlock className="mt-1.5">
            <CodeBlockCode
              code={data.code}
              language={data.language || "javascript"}
            />
          </CodeBlock>
        ) : (
          <pre className="mt-1.5 overflow-x-auto rounded-md border border-gray-200 bg-gray-50 px-2.5 py-1.5 font-mono text-[11px] leading-5 text-gray-700">
            {data.command}
          </pre>
        ))}
    </div>
  );
}

/**
 * prompt-kit "chain-of-thought" style multi-agent trace: collapsible trigger,
 * one node per agent on a vertical rail, with that agent's live steps below.
 * Steps: [{ title, detail, status: "running"|"done"|"error", tool_name, agent }]
 */
const ReasoningSteps = ({
  steps = [],
  defaultOpen = false,
  live = false,
  title,
  liveTitle,
}) => {
  const [open, setOpen] = useState(defaultOpen || live);

  // Stay open while streaming; collapse once the run finishes.
  useEffect(() => {
    setOpen(live || defaultOpen);
  }, [live, defaultOpen]);

  if (!steps.length) return null;

  const groups = groupByAgent(steps);
  const agentsUsed = [
    ...new Set(groups.map((g) => g.agent).filter((a) => a !== "manager")),
  ];
  const triggerLabel = live
    ? liveTitle || "Agents working..."
    : title ||
    (agentsUsed.length > 0
      ? `Agent run · ${agentsUsed
        .map((a) => AGENT_META[a]?.label || a)
        .join(", ")}`
      : "Agent run");

  return (
    <div className="mb-2 w-full">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="group inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 transition-colors hover:text-gray-900 dark:hover:text-gray-100 cursor-pointer"
      >
        {live ? (
          <Loader2 size={13} className="animate-spin text-secondary" />
        ) : (
          <Bot size={13} />
        )}
        <span className="text-gray-500 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-gray-100">{triggerLabel}</span>
        {open ? (
          <ChevronDown
            size={14}
            className="transition-transform text-gray-500 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-gray-100"
            aria-hidden="true"
          />
        ) : (
          <ChevronRight
            size={14}
            className="transition-transform text-gray-500 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-gray-100"
            aria-hidden="true"
          />
        )}
      </button>

      {open && (
        <div className="mt-2 space-y-0.5">
          {groups.map((group, groupIndex) => {
            const meta = AGENT_META[group.agent] || {
              label: group.agent,
              Icon: Bot,
            };
            const isLastGroup = groupIndex === groups.length - 1;
            const groupRunning =
              live &&
              isLastGroup &&
              group.steps[group.steps.length - 1]?.status === "running";

            return (
              <div key={`${group.agent}-${groupIndex}`} className="flex gap-2.5">
                {/* Rail: node icon + connector line */}
                <div className="flex flex-col items-center">
                  <span
                    className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border ${groupRunning
                      ? "border-secondary/40 bg-secondary/10 text-secondary"
                      : "border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400"
                      }`}
                  >
                    <meta.Icon size={13} />
                  </span>
                  {!isLastGroup && (
                    <span className="w-px flex-1 bg-gray-200 dark:bg-gray-700" aria-hidden="true" />
                  )}
                </div>

                {/* Agent label + its steps */}
                <div className="min-w-0 flex-1 pb-3">
                  <p
                    className={`text-xs font-semibold leading-6 ${groupRunning ? "text-gray-900 dark:text-gray-100" : "text-gray-700 dark:text-gray-300"
                      }`}
                  >
                    {meta.label}
                    {groupRunning && (
                      <span className="ml-1.5 font-normal text-secondary">
                        working...
                      </span>
                    )}
                  </p>
                  <div className="mt-0.5 space-y-1">
                    {group.steps.map((step, stepIndex) => {
                      const isLastStep =
                        isLastGroup && stepIndex === group.steps.length - 1;
                      const running =
                        live && isLastStep && step.status === "running";
                      return (
                        <div key={`${step.title}-${stepIndex}`}>
                          <div className="flex items-start gap-1.5">
                            <span className="mt-1.5 flex h-2 w-2 shrink-0 items-center justify-center">
                              {step.status === "error" ? (
                                <CircleAlert size={11} className="text-red-500" />
                              ) : running ? (
                                <Loader2
                                  size={11}
                                  className="animate-spin text-secondary"
                                />
                              ) : (
                                <span className="h-1 w-1 rounded-full bg-gray-300" />
                              )}
                            </span>
                            <p
                              className={`text-xs leading-5 ${step.status === "error"
                                ? "text-red-500 dark:text-red-400"
                                : running
                                  ? "animate-pulse text-gray-700 dark:text-gray-200"
                                  : "text-gray-500 dark:text-gray-400"
                                }`}
                            >
                              {step.title}
                              {step.detail ? (
                                <span className="text-gray-400 dark:text-gray-500">
                                  {" "}
                                  — {step.detail}
                                </span>
                              ) : null}
                            </p>
                          </div>
                          {step.data ? (
                            <div className="ml-3.5">
                              <StepData data={step.data} />
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ReasoningSteps;
